/* eslint-disable no-nested-ternary */
import React, { useContext, useEffect, useState } from "react";

import { Grid, MenuItem, SelectChangeEvent } from "@mui/material";
import { useTranslation } from "react-i18next";
import { FiInfo } from "react-icons/fi";
import { useHistory } from "react-router-dom";

import ConfirmationModal from "../../../../../../components/Dialogs/ConfirmationModal";
import { Button, Select } from "../../../../../../components/Form";
import AutoSuggest from "../../../../../../components/Form/AutoSuggest";
import { Color } from "../../../../../../components/Form/Button";
import LoadingModal from "../../../../../../components/LoadingModal";
import { useAppState } from "../../../../../../context/StateContext";
import { Job } from "../../../../../../models/job";
import { ItemJob } from "../../../../../../models/sales";
import { StorageBox } from "../../../../../../models/storage.box";
import { Warehouse } from "../../../../../../models/warehouse";
import { WarehouseMap } from "../../../../../../models/warehouse.map";
import { MapCell } from "../../../../../../models/warehouse.mapcell";
import { WarehouseSection } from "../../../../../../models/warehouse.section";
import { MovementContext } from "../../context/MovementContext";
import { TypeOfObject, TypesOfObjects, useActions } from "../../hooks/useAction";
import { SubTitle } from "../SubTitle";
import {
  ConfirmationModalContent,
  ContainerButtons,
  ContainerForms,
  ContainerObjectToMove,
  ContainerWhereToMove,
  SelectContainers,
} from "./styles";

export const Forms = () => {
  const { warehouse, warehouseList: warehouses } = useAppState();
  const {
    orderSelected,
    setOrderSelected,
    itemJobSelected,
    setItemJobSelected,
    storageBoxSelected,
    setStorageBoxSelected,
    mapSelected,
    setMapSelected,
    mapCellSelected,
    setMapCellSelected,
  } = useContext(MovementContext);

  const {
    getItemJobById,
    getOrders,
    getItemsJobsInMapCell,
    getStorageBoxById,
    getStorageBoxes,
    getStorageBoxInMapCell,
    itemsJobsInMapCell,
    moveItemJob,
    moveStorageBox,
    openConfirmationModal,
    setItemJobId,
    setOpenConfirmationModal,
    setStorageBoxId,
    storageBoxesInMapCell,
    getMapById,
    setMapId,
    getMapCells,
  } = useActions();

  const types: TypeOfObject[] = [
    {
      name: TypesOfObjects.STORAGE_BOX,
      value: "storageBox",
    },
    {
      name: TypesOfObjects.LOOSE_ITEM,
      value: "itemJob",
    },
  ];

  const { t: translate } = useTranslation();
  const history = useHistory<{ type: string; storageBoxId: number }>();

  const [warehouseSelected, setWarehouseSelected] = useState<Warehouse>();
  const [sectionSelected, setSectionSelected] = useState<WarehouseSection>();
  const [objectInMapCell, setObjectInMapCell] = useState<string | undefined>();
  const [typeOfObject, setTypeOfObject] = useState<TypeOfObject>(types[0]);
  const [searchValue, setSearchValue] = useState<string | undefined>(undefined);
  const [searchLocationValue, setSearchLocationValue] = useState<string | undefined>(undefined);

  const getDefaults = () => {
    let warehouseSelect: Warehouse | undefined;
    let section: WarehouseSection | undefined;
    let map: WarehouseMap | undefined;
    const warehouseItem = storageBoxSelected
      ? storageBoxSelected?.warehouses?.find((item) => item.id === warehouse?.id)
      : itemJobSelected?.mapCells?.find((cell) => cell.map?.warehouse?.id === warehouse?.id)?.map
          ?.warehouse;

    if (warehouseItem) {
      map = storageBoxSelected
        ? storageBoxSelected?.mapCells?.find(
            (cell) => cell.map?.warehouse?.id === warehouseItem?.id
          )?.map
        : itemJobSelected?.mapCells?.find((cell) => cell.map?.warehouse?.id === warehouseItem?.id)
            ?.map;

      warehouseSelect = warehouses?.find((item) => item.id === warehouseItem.id);
    }

    if (warehouseSelect) {
      section = warehouseSelect.sections?.find((section) => section.id === map?.section?.id);
    }

    return {
      map,
      section,
      warehouse: warehouseSelect,
    };
  };

  const clearAllFields = () => {
    setTypeOfObject(types[0]);
    setItemJobSelected(undefined);
    setStorageBoxSelected(undefined);
    setOrderSelected(undefined);
    setWarehouseSelected(undefined);
    setSectionSelected(undefined);
    setMapSelected(undefined);
    setMapCellSelected(undefined);
    setStorageBoxId(undefined);
    setItemJobId(undefined);
    setMapId(undefined);
  };

  const clearFollowingFields = () => {
    setWarehouseSelected(undefined);
    setSectionSelected(undefined);
    setMapSelected(undefined);
    setMapCellSelected(undefined);
    setMapId(undefined);
    setStorageBoxId(undefined);
    setItemJobId(undefined);
  };

  const { data: storageBoxResponse, isFetching: fetchingStorageBox } = getStorageBoxById();

  const { data: itemJobResponse, isFetching: fetchingItemJob } = getItemJobById();

  const { isFetching: fetchingStorageBoxesInMapCell } = getStorageBoxInMapCell(mapCellSelected?.id);
  const { isFetching: fetchingItemsJobsInMapCell } = getItemsJobsInMapCell(mapCellSelected?.id);
  const { mutate: mutateStorageBox, isLoading: loadingMoveStorageBox } =
    moveStorageBox(clearAllFields);
  const { mutate: mutateItemJob, isLoading: loadingMoveItemJob } = moveItemJob(clearAllFields);

  const { data: mapResponse, isFetching: fetchingMap } = getMapById();

  useEffect(() => {
    if (!fetchingStorageBox && !fetchingItemJob) {
      setItemJobSelected(undefined);
      setOrderSelected(undefined);
      setStorageBoxSelected(undefined);
      setWarehouseSelected(undefined);
      setSectionSelected(undefined);
      setMapSelected(undefined);
      setMapCellSelected(undefined);
      setMapId(undefined);
      setStorageBoxId(undefined);
      setItemJobId(undefined);
    }
  }, [typeOfObject, warehouse]);

  const renderObjectToMoveSelect = () => {
    const handleSetTypeOfObject = (value: string) => {
      const selected = types?.filter((type) => type?.name === value)?.[0];

      if (selected) setTypeOfObject(selected);
    };

    return (
      <Grid item lg={3} md={12} sm={12} xs={12}>
        <SelectContainers>
          <Select
            label={translate("typeOfObject")}
            placeholder={translate("select")}
            value={typeOfObject?.name || ""}
            onChange={(event: SelectChangeEvent<any>) => {
              handleSetTypeOfObject(event.target.value);
              setItemJobSelected(undefined);
              setStorageBoxSelected(undefined);
            }}
            dataCy="select-type-of-object"
          >
            {types?.map((type: TypeOfObject) => (
              <MenuItem
                key={type?.name}
                value={type?.name}
                data-cy="select-menu-item-type-of-object"
              >
                {translate(type?.name)}
              </MenuItem>
            ))}
          </Select>
        </SelectContainers>
      </Grid>
    );
  };

  const renderLooseItemSelect = () => {
    if (typeOfObject?.name === TypesOfObjects.LOOSE_ITEM) {
      return (
        <Grid item xs={12}>
          <SelectContainers>
            <Select
              label={translate("item")}
              placeholder={translate("select")}
              value={itemJobSelected?.id || ""}
              disabled={!orderSelected}
              onChange={(event) => {
                const { value: itemId } = event.target;

                setItemJobId(Number(itemId) || undefined);
                if (!itemId) {
                  setItemJobSelected(undefined);
                  clearFollowingFields();
                }
              }}
              dataCy="select-type-of-object"
            >
              {orderSelected?.itemsJob
                ?.sort((itemA, itemB) => {
                  if (
                    String(itemA?.description || "").toLowerCase() >
                    String(itemB?.description || "").toLowerCase()
                  )
                    return 1;
                  if (
                    String(itemA?.description || "").toLowerCase() <
                    String(itemB?.description || "").toLowerCase()
                  )
                    return -1;

                  return 0;
                })
                ?.sort((a, b) => (a?.number && b?.number ? a.number - b.number : 0))
                .map((itemJob: ItemJob) => (
                  <MenuItem key={itemJob?.id} value={itemJob?.id} data-cy="select-loose-item">
                    {itemJob?.number} - {itemJob?.description}
                  </MenuItem>
                ))}
            </Select>
          </SelectContainers>
        </Grid>
      );
    }

    return null;
  };

  const renderObjectSearchAutoSuggest = () => {
    const getSuggestInput = () => {
      if (typeOfObject?.name === TypesOfObjects.LOOSE_ITEM) {
        return (
          <AutoSuggest
            label={translate("order")}
            placeholder={translate("search")}
            noOptionsText={
              !searchValue ? translate("enterTheOrderToSearch") : translate("noResults")
            }
            filterOptions={(options) => {
              const filteredOptions: Job[] = [];

              options.forEach((option: Job) => {
                if (option?.itemsJob && option?.itemsJob?.length > 0) filteredOptions.push(option);
              });

              return filteredOptions;
            }}
            getOptionLabel={(option: Job) =>
              `${option?.reference || ""}${
                option?.sequence && Number(option?.sequence) > 0 ? `/${option?.sequence}` : ""
              } - ${option?.customer?.name || ""} ${option?.customer?.lastName || ""}`
            }
            getOptionSelected={(option: Job, value: Job) => option?.id === value?.id}
            inputValue={
              orderSelected
                ? `${orderSelected?.reference || ""}${
                    orderSelected?.sequence && Number(orderSelected?.sequence) > 0
                      ? `/${orderSelected?.sequence}`
                      : ""
                  } - ${orderSelected?.customer?.name || ""} ${
                    orderSelected?.customer?.lastName || ""
                  }`
                : ""
            }
            value={orderSelected}
            onChange={(value: Job) => {
              setOrderSelected(value || undefined);
              if (!value?.id) {
                setSearchValue(undefined);
                setSearchLocationValue(undefined);
                setOrderSelected(undefined);
                setItemJobSelected(undefined);
                clearFollowingFields();
              }
            }}
            onSearch={(value) => {
              setSearchValue(value);

              return getOrders([{ referenceOrCustomer: value }]);
            }}
            dataCy="auto-suggest-orders"
            clearOptionsOnEscape={true}
          />
        );
      }

      if (typeOfObject?.name === TypesOfObjects.STORAGE_BOX) {
        return (
          <AutoSuggest
            label={translate("storageBox")}
            placeholder={translate("search")}
            noOptionsText={
              !searchValue ? translate("enterTheBoxNumberToSearch") : translate("noResults")
            }
            filterOptions={(options) => {
              const filteredOptions: StorageBox[] = [];

              options.forEach((option) => {
                const warehouseIds = option.warehouses?.map((item) => item.id);

                if (option.warehouseId === warehouse?.id || warehouseIds?.includes(warehouse?.id))
                  filteredOptions.push(option);
              });

              return filteredOptions;
            }}
            getOptionLabel={(option: StorageBox) => `${option.number ?? ""}`}
            getOptionSelected={(option: StorageBox, value: StorageBox) => option?.id === value?.id}
            inputValue={storageBoxSelected?.number}
            value={storageBoxSelected}
            onChange={(value: StorageBox) => {
              setStorageBoxId(value?.id);
              if (!value?.id) {
                setSearchValue(undefined);
                setSearchLocationValue(undefined);
                setStorageBoxSelected(undefined);
                clearFollowingFields();
              }
            }}
            onSearch={(value) => {
              setSearchValue(value);

              return getStorageBoxes([{ number: value }]);
            }}
            dataCy="auto-suggest-storage-boxes"
            clearOptionsOnEscape={true}
          />
        );
      }

      return (
        <AutoSuggest
          label={translate("object")}
          placeholder={translate("search")}
          disabled={!typeOfObject}
          dataCy="auto-suggest-disable"
        />
      );
    };

    return (
      <Grid item lg={9} md={12} sm={12} xs={12}>
        <SelectContainers>{getSuggestInput()}</SelectContainers>
      </Grid>
    );
  };

  const onSetIfWarehouseIsCapable = (warehouse?: Warehouse): boolean => {
    if (!warehouse) return false;

    const sections = warehouse.sections || [];
    let isWarehouseCapable: boolean = false;

    if (sections.length) {
      sections.forEach((section) => {
        const isCapable = onGetIfSectionIsCapable(section);

        if (isCapable) isWarehouseCapable = isCapable;
      });
    }

    return isWarehouseCapable;
  };

  const onGetIfSectionIsCapable = (section?: WarehouseSection): boolean => {
    if (!section) return false;

    if (typeOfObject?.name === TypesOfObjects.STORAGE_BOX) {
      const storageBoxType = section.storageBoxTypes;

      if (storageBoxType && storageBoxType.length) {
        const storageBoxTypeIds = storageBoxType.map((storageBoxType) => storageBoxType.id);

        if (storageBoxTypeIds.includes(storageBoxSelected?.storageBoxTypeId)) return true;
      }
    } else {
      return section.allowsItemLoose;
    }

    return false;
  };

  const onSortWarehouse = (warehouseArr?: Warehouse[]): Warehouse[] => {
    if (!warehouseArr) return [];

    return warehouseArr?.sort((warehouseOne, warehouseTwo) =>
      (warehouseOne?.name || "") > (warehouseTwo?.name || "")
        ? 1
        : (warehouseTwo?.name || "") > (warehouseOne?.name || "")
        ? -1
        : 0
    );
  };

  const renderWarehouseSelect = () => {
    const handleSetWarehouse = (target: any) => {
      let selected: Warehouse | undefined;

      if (typeOfObject?.name === TypesOfObjects.LOOSE_ITEM) {
        selected = warehouses?.filter((warehouse) => target?.value === warehouse?.id)?.[0];
        if (selected) return setWarehouseSelected(selected);
      }

      if (typeOfObject?.name === TypesOfObjects.STORAGE_BOX) {
        selected = storageBoxSelected?.warehouses?.filter(
          (warehouse) => target?.value === warehouse?.id
        )?.[0];
        if (selected) return setWarehouseSelected(selected);
      }

      return setWarehouseSelected(undefined);
    };

    const handleSelects = () => {
      if (typeOfObject?.name === TypesOfObjects.LOOSE_ITEM) {
        return warehouses?.map((warehouse) => (
          <MenuItem
            key={warehouse?.id}
            value={warehouse?.id}
            data-cy="select-menu-item-warehouse-item-job"
          >
            {translate(warehouse?.name || "")}
          </MenuItem>
        ));
      }

      if (typeOfObject?.name === TypesOfObjects.STORAGE_BOX) {
        return onSortWarehouse(storageBoxSelected?.warehouses)?.map?.((warehouse) => {
          const warehouseHasCapableSection = onSetIfWarehouseIsCapable(warehouse);

          if (warehouseHasCapableSection) {
            return (
              <MenuItem
                key={warehouse?.id}
                value={warehouse?.id}
                data-cy="select-menu-item-warehouse-storage-box"
              >
                {translate(warehouse?.name || "")}
              </MenuItem>
            );
          }

          return null;
        });
      }

      return [];
    };

    return (
      <Grid>
        <SelectContainers>
          <Select
            label={translate("warehouse")}
            value={warehouseSelected?.id || ""}
            placeholder={translate("select")}
            onChange={({ target }) => {
              handleSetWarehouse(target);
              setSectionSelected(undefined);
              setMapSelected(undefined);
              setMapCellSelected(undefined);
            }}
            disabled={!storageBoxSelected && !itemJobSelected}
            dataCy="select-warehouse"
          >
            {handleSelects()}
          </Select>
        </SelectContainers>
      </Grid>
    );
  };

  const renderSectionSelect = () => {
    const handleSetSection = (target: any) => {
      const selected = warehouseSelected?.sections?.filter(
        (section) => section?.id === target?.value
      )?.[0];

      if (selected) setSectionSelected(selected);
    };

    const getSections = (): WarehouseSection[] => {
      if (typeOfObject?.name === TypesOfObjects.LOOSE_ITEM) {
        return warehouseSelected?.sections?.filter((section) => section?.allowsItemLoose) || [];
      }

      if (typeOfObject?.name === TypesOfObjects.STORAGE_BOX) {
        return (
          warehouseSelected?.sections?.filter(
            (section) => section?.allowsStorageBox && onGetIfSectionIsCapable(section)
          ) || []
        );
      }

      return [] as WarehouseSection[];
    };

    return (
      <Grid>
        <SelectContainers>
          <Select
            label={translate("section")}
            value={sectionSelected?.id || ""}
            placeholder={translate("select")}
            onChange={({ target }) => {
              handleSetSection(target);
              setMapSelected(undefined);
              setMapCellSelected(undefined);
            }}
            disabled={!warehouseSelected?.sections?.length}
            dataCy="select-section"
          >
            {getSections()?.map((section) => (
              <MenuItem key={section?.id} value={section?.id} data-cy="select-menu-item-section">
                {translate(section?.name || "")}
              </MenuItem>
            ))}
          </Select>
        </SelectContainers>
      </Grid>
    );
  };

  const renderMapSelect = () => {
    const handleSetMap = (map: WarehouseMap) => {
      setMapId(map?.id);
      if (!map) setMapSelected(undefined);
    };

    const getMaps = () => {
      if (!sectionSelected) return [];

      return sectionSelected?.maps?.filter((map) => map?.isActive);
    };

    return (
      <Grid item md={6}>
        <SelectContainers>
          <AutoSuggest
            label={translate("maps")}
            value={mapSelected}
            placeholder={translate("select")}
            options={getMaps()}
            getOptionLabel={(option: WarehouseMap) => option?.name}
            disabled={!sectionSelected?.maps?.length}
            dataCy="select-map"
            onChange={(value: WarehouseMap) => {
              handleSetMap(value);
              setMapCellSelected(undefined);
            }}
            inputValue={mapSelected?.name}
          />
        </SelectContainers>
      </Grid>
    );
  };

  const renderMoveToLocationAutoSuggest = () => {
    return (
      <Grid item md={6}>
        <SelectContainers>
          <AutoSuggest
            label={translate("moveToLocation")}
            placeholder={translate("search")}
            noOptionsText={
              !searchLocationValue ? translate("enterTheLocationToSearch") : translate("noResults")
            }
            getOptionLabel={(option: MapCell) => {
              if (!option?.name) return "";

              return mapSelected ? option.name : `${option.name} (${option?.map?.name})`;
            }}
            getOptionSelected={(option: MapCell, value: MapCell) => option?.id === value?.id}
            filterOptions={(options) => options}
            inputValue={mapCellSelected?.name}
            clearOptionsOnEscape
            disabled={!sectionSelected}
            onChange={(value: MapCell) => setMapCellSelected(value)}
            onSearch={(value) => {
              setSearchLocationValue(value);

              return getMapCells(value, sectionSelected?.id, mapSelected?.id);
            }}
            dataCy="auto-suggest-move-to-location"
          />
        </SelectContainers>
      </Grid>
    );
  };

  const validateMapCellBeforeMove = () => {
    if (itemsJobsInMapCell?.length > 0) {
      setOpenConfirmationModal(true);
      setObjectInMapCell(itemsJobsInMapCell?.[0]?.description);

      return true;
    }

    if (storageBoxesInMapCell?.length > 0) {
      setOpenConfirmationModal(true);
      setObjectInMapCell(storageBoxesInMapCell?.[0]?.number);

      return true;
    }

    return false;
  };

  const handleSubmit = () => {
    if (mapCellSelected) {
      if (itemJobSelected) {
        mutateItemJob({
          itemJob: itemJobSelected,
          mapCell: mapCellSelected,
        });
      }
      if (storageBoxSelected) {
        mutateStorageBox({
          storageBox: storageBoxSelected,
          mapCell: mapCellSelected,
        });
      }
    }
  };

  const renderButtonsMoveAndCancel = () => {
    const buttonMove = () => {
      const disableButton = () => {
        if (!mapCellSelected) return true;
        if (!itemJobSelected && !storageBoxSelected) return true;
        if (loadingMoveItemJob || loadingMoveStorageBox) return true;
        if (fetchingStorageBoxesInMapCell || fetchingItemsJobsInMapCell) return true;

        return false;
      };

      return (
        <Button
          onClick={() => {
            if (!validateMapCellBeforeMove()) {
              handleSubmit();
            }
          }}
          loading={loadingMoveItemJob || loadingMoveStorageBox}
          customColor={Color.SUCCESS}
          disabled={disableButton()}
          data-cy="button-move"
        >
          {translate("move")}
        </Button>
      );
    };

    const buttonClear = () => {
      return (
        <Button onClick={() => clearAllFields()} customColor={Color.DARK} data-cy="button-clear">
          {translate("clear")}
        </Button>
      );
    };

    return (
      <ContainerButtons>
        {buttonClear()}
        {buttonMove()}
      </ContainerButtons>
    );
  };

  const renderConfirmationModalContent = () => {
    return (
      <div>
        <ConfirmationModalContent>
          {translate("the")} <span>{objectInMapCell}</span>{" "}
          {translate("isPlacedInThisLocationRightNowWouldYouLikeToMoveItToAnUnknowLocationForNow")}
        </ConfirmationModalContent>
      </div>
    );
  };

  useEffect(() => {
    const { state } = history.location;

    if (state) {
      setTypeOfObject({ name: TypesOfObjects.STORAGE_BOX, value: state.type });
      setStorageBoxId(state.storageBoxId);
    }
  }, [history.location]);

  useEffect(() => {
    if (itemJobResponse && itemJobResponse.data) setItemJobSelected(itemJobResponse.data);
    if (storageBoxResponse && storageBoxResponse.data)
      setStorageBoxSelected(storageBoxResponse.data);
    if (mapResponse && mapResponse.data) setMapSelected(mapResponse.data);
  }, [itemJobResponse, storageBoxResponse, mapResponse]);

  useEffect(() => {
    if (!itemJobSelected && !storageBoxSelected) setWarehouseSelected(undefined);
    if (itemJobSelected || storageBoxSelected) {
      const { warehouse } = getDefaults();

      clearFollowingFields();

      setWarehouseSelected(warehouse);

      if (!warehouse) {
        if (typeOfObject?.name === TypesOfObjects.STORAGE_BOX) {
          const availableWarehouses = storageBoxSelected?.warehouses?.filter((warehouse) =>
            onSetIfWarehouseIsCapable(warehouse)
          );

          if (availableWarehouses?.length === 1) {
            setWarehouseSelected(availableWarehouses?.[0]);
          }
        }

        if (typeOfObject?.name === TypesOfObjects.LOOSE_ITEM && warehouses?.length === 1) {
          setWarehouseSelected(warehouses?.[0]);
        }
      }
    }
  }, [itemJobSelected, storageBoxSelected]);

  useEffect(() => {
    if (typeOfObject?.name === TypesOfObjects.STORAGE_BOX) {
      const availableSections = warehouseSelected?.sections?.filter(
        (section) => section?.allowsStorageBox && onGetIfSectionIsCapable(section)
      );

      if (availableSections?.length === 1) {
        setSectionSelected(availableSections?.[0]);
      }
    }

    if (typeOfObject?.name === TypesOfObjects.LOOSE_ITEM) {
      const availableSections = warehouseSelected?.sections?.filter(
        (section) => section?.allowsItemLoose
      );

      if (availableSections?.length === 1) {
        setSectionSelected(availableSections?.[0]);
      }
    }
  }, [warehouseSelected]);

  useEffect(() => {
    const availableMaps = sectionSelected?.maps?.filter((map) => map?.isActive);

    if (availableMaps?.length === 1) {
      setMapId(availableMaps?.[0]?.id);
    }
  }, [sectionSelected]);

  return (
    <ContainerForms>
      <ContainerObjectToMove>
        <SubTitle text={translate("move")} />

        <Grid container direction="row" spacing={2}>
          {renderObjectToMoveSelect()}
          {renderObjectSearchAutoSuggest()}
          {renderLooseItemSelect()}
        </Grid>
      </ContainerObjectToMove>

      <ContainerWhereToMove>
        <SubTitle text={translate("moveTo")} />

        {renderWarehouseSelect()}
        {renderSectionSelect()}
        <Grid container direction="row" spacing={2}>
          {renderMapSelect()}
          {renderMoveToLocationAutoSuggest()}
        </Grid>
        {renderButtonsMoveAndCancel()}
      </ContainerWhereToMove>

      <ConfirmationModal
        open={openConfirmationModal}
        closeModal={() => {
          setOpenConfirmationModal(false);
        }}
        customColorSave={Color.WARNING}
        textSubmit={translate("move")}
        type={"warning"}
        title={translate("locationIsFull")}
        submit={handleSubmit}
        loading={loadingMoveStorageBox || loadingMoveItemJob}
        width="32.5rem !important"
        leftIconTitle={<FiInfo color="#F79009" />}
        data-cy="confirmation-modal"
      >
        {renderConfirmationModalContent()}
      </ConfirmationModal>

      <LoadingModal showing={fetchingStorageBox || fetchingItemJob || fetchingMap} />
    </ContainerForms>
  );
};
